'use client';

import React, { useState, useEffect } from 'react';
import { fetchCustomerRiskFrequency } from '../../apiHelper';
import { AlertTriangle, ArrowRight, ShieldAlert } from 'lucide-react';

const CustomerRiskDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadRiskData = async () => {
            try {
                const response = await fetchCustomerRiskFrequency({
                    tier1_min_spend: 100000,
                    lookback_days: 60
                });
                setData(response);
            } catch (err) {
                console.error("Error fetching risk data:", err);
            } finally {
                setLoading(false);
            }
        };
        loadRiskData();
    }, []);

    // Pulsating Skeleton State
    if (loading) return (
        <div className="flex flex-col h-full animate-pulse">
            {/* Header Skeleton */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-200 rounded-2xl" />
                    <div className="space-y-2">
                        <div className="h-5 w-32 bg-gray-200 rounded" />
                        <div className="h-3 w-24 bg-gray-100 rounded" />
                    </div>
                </div>
                <div className="h-6 w-16 bg-gray-200 rounded-full" />
            </div>

            {/* List Skeleton */}
            <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="p-4 border border-gray-100 rounded-2xl bg-white">
                        <div className="flex justify-between mb-4">
                            <div className="flex gap-3">
                                <div className="w-10 h-10 bg-gray-100 rounded-full" />
                                <div className="space-y-2">
                                    <div className="h-4 w-24 bg-gray-100 rounded" />
                                    <div className="h-3 w-12 bg-gray-50 rounded" />
                                </div>
                            </div>
                            <div className="h-4 w-12 bg-gray-100 rounded" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="h-10 bg-gray-50 rounded-xl" />
                            <div className="h-10 bg-gray-50 rounded-xl" />
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer Skeleton */}
            <div className="mt-auto pt-6">
                <div className="h-16 w-full bg-gray-50 rounded-2xl" />
            </div>
        </div>
    );

    const customers = data?.at_risk_customers || [];
    const summary = data?.summary || {};

    return (
        <div className="flex flex-col h-full">
            {/* Header with Alert Badge */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-red-50 text-red-600 rounded-2xl">
                        <ShieldAlert size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Customer Health</h3>
                        <p className="text-xs text-gray-500">{summary.at_risk_count} customers require attention</p>
                    </div>
                </div>
                <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold">
                    HIGH RISK
                </div>
            </div>

            {/* Risk List */}
            <div className="space-y-4 overflow-y-auto pr-2 max-h-[500px] custom-scrollbar">
                {customers.map((customer) => (
                    <div
                        key={customer.customer_id}
                        className="group p-4 bg-white border border-gray-100 rounded-2xl hover:border-red-200 hover:shadow-md transition-all cursor-pointer"
                    >
                        <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-bold">
                                    {customer.customer_name.charAt(0)}
                                </div>
                                <div>
                                    <p className="font-bold text-gray-900 text-sm leading-tight">{customer.customer_name}</p>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{customer.tier}</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className={`text-xs font-bold ${customer.risk_score > 80 ? 'text-red-600' : 'text-orange-500'}`}>
                                    {customer.risk_score}% Risk
                                </div>
                                <div className="w-16 h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                    <div
                                        className={`h-full rounded-full ${customer.risk_score > 80 ? 'bg-red-500' : 'bg-orange-400'}`}
                                        style={{ width: `${customer.risk_score}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="bg-gray-50 p-2 rounded-xl text-center">
                                <p className="text-[10px] text-gray-400 uppercase">Freq. Drop</p>
                                <p className="text-sm font-bold text-red-600">-{customer.frequency_drop_percent}%</p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-xl text-center">
                                <p className="text-[10px] text-gray-400 uppercase">Last Order</p>
                                <p className="text-sm font-bold text-gray-700">{customer.days_since_last_transaction}d ago</p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${customer.status === 'Churn Warning' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                                }`}>
                                {customer.status.toUpperCase()}
                            </span>
                            <button className="text-[#4285F4] text-[10px] font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                                VIEW PROFILE <ArrowRight size={12} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer Summary */}
            <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="bg-blue-50 rounded-2xl p-4 flex items-start gap-3">
                    <AlertTriangle className="text-blue-600 shrink-0" size={18} />
                    <p className="text-xs text-blue-800 leading-relaxed">
                        <b>Insight:</b> Customers are visiting <b>{summary.avg_frequency_threshold}x</b> less than their historical average.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CustomerRiskDashboard;